import React, { useState } from 'react'
import styled from 'styled-components'
import { institutionalColors } from '~/design'

const ExportButton = styled.button`
  padding: 8px 16px;
  background: #0f438a;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s ease;

  &:hover {
    background: #0d3a75;
  }

  &:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }
`
const ExportModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
`
const ExportModalContent = styled.div`
  background: white;
  padding: 24px;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  position: relative;
  z-index: 1000000;
`

const ExportModalHeader = styled.h2`
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${institutionalColors.neutral30};
`

const ExportModalBody = styled.div`
  margin-bottom: 24px;

  p {
    margin: 0 0 16px 0;
    color: #666;
    font-size: 14px;
  }
`

const ExportRadioLabel = styled.label`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background 0.2s ease;

  &:hover {
    background: #f8f9fa;
  }

  input {
    margin-right: 8px;
    cursor: pointer;
  }
`

const ExportModalFooter = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`

const ExportCancelButton = styled.button`
  padding: 8px 16px;
  background: #f3f4f6;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${institutionalColors.neutral30};
  transition: background 0.2s ease;

  &:hover {
    background: #e5e7eb;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

const LoadingMessage = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: #f0f7ff;
  border-radius: 6px;
  color: #0f438a;
  font-size: 14px;
  text-align: center;
`

interface ExportInsightModalProps {
  isOpen: boolean
  totalCount: number
  exportFilters: {
    insightIds?: string[]
    search?: string
    delimitation?: string
    variableKeys?: string[]
    variablePresetKeys?: string[]
    alertType?: string[]
    alertPreset?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }
  onClose: () => void
  onExport: (format: 'xlsx' | 'csv') => void
  isExporting: boolean
}

interface ExportInsightButtonProps {
  onClick: () => void
  disabled?: boolean
}

export function ExportInsightModal({
  isOpen,
  totalCount,
  onClose,
  onExport,
  isExporting,
}: ExportInsightModalProps) {
  const [exportFormat, setExportFormat] = useState<'xlsx' | 'csv'>('xlsx')

  const handleExport = () => {
    onExport(exportFormat)
  }

  if (!isOpen) return null

  return (
    <ExportModalOverlay onClick={() => !isExporting && onClose()}>
      <ExportModalContent onClick={e => e.stopPropagation()}>
        <ExportModalHeader>Exportar insights</ExportModalHeader>

        <ExportModalBody>
          <p>
            Serão exportados <strong>{totalCount.toLocaleString('pt-BR')}</strong> insights
          </p>

          <div>
            <ExportRadioLabel>
              <input
                type='radio'
                value='xlsx'
                checked={exportFormat === 'xlsx'}
                onChange={() => setExportFormat('xlsx')}
                disabled={isExporting}
              />
              <div>
                <strong>Excel (.xlsx)</strong>
                <span style={{ color: '#666', fontSize: '13px', marginLeft: '6px' }}>
                  - Recomendado
                </span>
              </div>
            </ExportRadioLabel>

            <ExportRadioLabel>
              <input
                type='radio'
                value='csv'
                checked={exportFormat === 'csv'}
                onChange={() => setExportFormat('csv')}
                disabled={isExporting}
              />
              <div>
                <strong>CSV (.csv)</strong>
                <span style={{ color: '#666', fontSize: '13px', marginLeft: '6px' }}>
                  - Compatível com outros sistemas
                </span>
              </div>
            </ExportRadioLabel>
          </div>

          {isExporting && (
            <LoadingMessage>Gerando arquivo... Isso pode levar alguns segundos.</LoadingMessage>
          )}
        </ExportModalBody>

        <ExportModalFooter>
          <ExportCancelButton onClick={onClose} disabled={isExporting}>
            Cancelar
          </ExportCancelButton>
          <ExportButton onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Exportando...' : 'Exportar'}
          </ExportButton>
        </ExportModalFooter>
      </ExportModalContent>
    </ExportModalOverlay>
  )
}

export function ExportInsightButton({ onClick, disabled }: ExportInsightButtonProps) {
  return (
    <ExportButton onClick={onClick} disabled={disabled}>
      Exportar
    </ExportButton>
  )
}
